<template>
  <div id="vortec-logo-animation"></div>
</template>

<script setup>
import { onMounted, onUnmounted } from 'vue';
import * as THREE from 'three';
import { OrbitControls } from 'three-orbitcontrols-ts';

const createRenderer = () => {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.domElement.style.display = 'block';
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  return renderer;
};

const createScene = () => {
  const scene = new THREE.Scene();
  return scene;
};

const createCamera = () => {
  const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 2000);
  camera.position.set(0, 0, 90);
  return camera;
};

const createLights = () => {
  const ambientLight = new THREE.AmbientLight(0xffffff, 1);
  const mainLight = new THREE.DirectionalLight(0xffffff, 1.5);
  mainLight.position.set(10, 20, 25);
  return { ambientLight, mainLight };
};

const createControls = (camera, canvas) => {
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = false;
  controls.enablePan = false;
  controls.enableZoom = false;
  controls.enableRotate = false;
  controls.update();
  return {
    orbitControls: controls,
    tick: () => controls.update(),
  };
};

/* ---------- path builder (rounded rectangle centerline) ---------- */
function buildRoundedRectPath(width, height, cornerRadius, edgeSeg = 20, cornerSeg = 12) {
  const hw = width / 2;
  const hh = height / 2;
  const pts = [];
  const push = (x, y) => pts.push(new THREE.Vector3(x, y, 0));

  for (let i = 0; i <= edgeSeg; i++) {
    const t = i / edgeSeg;
    const x = -(hw - cornerRadius) + t * (2 * (hw - cornerRadius));
    const y = hh;
    push(x, y);
  }

  {
    const cx = hw - cornerRadius,
      cy = hh - cornerRadius;
    for (let j = 1; j <= cornerSeg; j++) {
      const theta = Math.PI / 2 - (j * (Math.PI / 2)) / cornerSeg;
      push(cx + cornerRadius * Math.cos(theta), cy + cornerRadius * Math.sin(theta));
    }
  }

  for (let i = 1; i <= edgeSeg; i++) {
    const t = i / edgeSeg;
    const x = hw;
    const y = hh - cornerRadius - t * (2 * (hh - cornerRadius));
    push(x, y);
  }

  {
    const cx = hw - cornerRadius,
      cy = -hh + cornerRadius;
    for (let j = 1; j <= cornerSeg; j++) {
      const theta = 0 - (j * (Math.PI / 2)) / cornerSeg;
      push(cx + cornerRadius * Math.cos(theta), cy + cornerRadius * Math.sin(theta));
    }
  }

  for (let i = 1; i <= edgeSeg; i++) {
    const t = i / edgeSeg;
    const x = hw - cornerRadius - t * (2 * (hw - cornerRadius));
    const y = -hh;
    push(x, y);
  }

  {
    const cx = -hw + cornerRadius,
      cy = -hh + cornerRadius;
    for (let j = 1; j <= cornerSeg; j++) {
      const theta = -Math.PI / 2 - (j * (Math.PI / 2)) / cornerSeg;
      push(cx + cornerRadius * Math.cos(theta), cy + cornerRadius * Math.sin(theta));
    }
  }

  for (let i = 1; i <= edgeSeg; i++) {
    const t = i / edgeSeg;
    const x = -hw;
    const y = -hh + cornerRadius + t * (2 * (hh - cornerRadius));
    push(x, y);
  }

  {
    const cx = -hw + cornerRadius,
      cy = hh - cornerRadius;
    for (let j = 1; j <= cornerSeg; j++) {
      const theta = Math.PI - (j * (Math.PI / 2)) / cornerSeg;
      push(cx + cornerRadius * Math.cos(theta), cy + cornerRadius * Math.sin(theta));
    }
  }

  return pts;
}

/* ---------- create tube of points around path using local frames ---------- */
const createRoundedRectTube = ({
  width = 52,
  height = 24,
  cornerRadius = 8,
  tubeRadius = 3.2,
  pathSeg = 200,
  circSeg = 14,
  color = '#FEFEFE',
  moveRange = 6,
  followSmoothing = 0.12,
  mouseRef = { targetX: 0, targetY: 0 },
  breathAmplitude = 0.16,
  breathSpeed = 0.7,
  perPointJitter = 0.02,
  fillInterior = false,
  radialPoints = 6,
  pinnedToWall = false,
  cameraRef = null,
  wallDistance = 60,
  separation = 1.25,
  moveAmp = 0.12,
  moveFreq = 0.9,
  tanAmp = 0.8,
  loadDurationDefault = 1.6,
  loadStaggerDefault = 0.0008,
  loadExitDurationDefault = 0.9,
  invertMouse = false,
} = {}) => {
  let lockBreathing = 0,
    lockedSize = null;
  const raw = buildRoundedRectPath(width, height, cornerRadius, 24, 12);

  let total = 0;
  for (let i = 1; i < raw.length; i++) total += raw[i].distanceTo(raw[i - 1]);
  total += raw[0].distanceTo(raw[raw.length - 1]);

  const getPosAtT = (t) => {
    const target = t * total;
    let acc = 0;
    for (let i = 0; i < raw.length; i++) {
      const a = raw[i];
      const b = raw[(i + 1) % raw.length];
      const segLen = a.distanceTo(b);
      if (target <= acc + segLen) {
        const localT = (target - acc) / segLen;
        return new THREE.Vector3().lerpVectors(a, b, localT);
      }
      acc += segLen;
    }
    return raw[raw.length - 1].clone();
  };

  const curvePoints = [];
  for (let i = 0; i < pathSeg; i++) curvePoints.push(getPosAtT(i / pathSeg));

  const centers = [];
  const dirs = [];
  const tangents = [];
  const baseRadii = [];
  const jitterFactors = [];
  const phases = [];
  const pts = [];
  const up = new THREE.Vector3(0, 0, 1);

  for (let i = 0; i < curvePoints.length; i++) {
    const prev = curvePoints[(i - 1 + curvePoints.length) % curvePoints.length];
    const next = curvePoints[(i + 1) % curvePoints.length];
    const center = curvePoints[i];

    const tangent = next.clone().sub(prev).normalize();

    let localUp = up;
    if (Math.abs(tangent.dot(localUp)) > 0.999) localUp = new THREE.Vector3(1, 0, 0);

    const normal = new THREE.Vector3().crossVectors(localUp, tangent).normalize();
    const binormal = new THREE.Vector3().crossVectors(tangent, normal).normalize();

    const circCount = Math.max(3, circSeg);
    for (let j = 0; j < circCount; j++) {
      const theta = (j / circCount) * Math.PI * 2;
      const offset = normal
        .clone()
        .multiplyScalar(Math.cos(theta) * tubeRadius)
        .add(binormal.clone().multiplyScalar(Math.sin(theta) * tubeRadius));

      const noiseFactor = 1 + (Math.random() - 0.5) * 0.05;
      const radius = offset.length() * noiseFactor * separation;

      const dir = offset.clone().normalize();
      const initialPos = center.clone().add(dir.clone().multiplyScalar(radius));

      centers.push(center.clone());
      dirs.push(dir.clone());
      baseRadii.push(radius);
      jitterFactors.push(1 + (Math.random() - 0.5) * perPointJitter);
      phases.push(Math.random() * Math.PI * 2);
      pts.push(initialPos);

      tangents.push(tangent.clone());
    }

    if (fillInterior) {
      for (let k = 0; k < radialPoints; k++) {
        const r = tubeRadius * Math.sqrt(Math.random()) * separation;
        const theta = Math.random() * Math.PI * 2;
        const offset = normal
          .clone()
          .multiplyScalar(Math.cos(theta) * r)
          .add(binormal.clone().multiplyScalar(Math.sin(theta) * r));

        const dir = offset.clone().normalize();
        const radius = offset.length();
        const initialPos = center.clone().add(offset);

        centers.push(center.clone());
        dirs.push(dir.clone());
        baseRadii.push(radius);
        jitterFactors.push(1 + (Math.random() - 0.5) * perPointJitter);
        phases.push(Math.random() * Math.PI * 2);
        pts.push(initialPos);

        tangents.push(tangent.clone());
      }
    }
  }

  const totalPoints = pts.length;
  const targets = new Float32Array(totalPoints * 3);
  for (let i = 0; i < totalPoints; i++) {
    targets[i * 3 + 0] = pts[i].x;
    targets[i * 3 + 1] = pts[i].y;
    targets[i * 3 + 2] = pts[i].z;
  }

  const positions = new Float32Array(totalPoints * 3);
  for (let i = 0; i < totalPoints; i++) {
    positions[i * 3 + 0] = targets[i * 3 + 0];
    positions[i * 3 + 1] = targets[i * 3 + 1];
    positions[i * 3 + 2] = targets[i * 3 + 2];
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.attributes.position.setUsage(THREE.DynamicDrawUsage);

  const colors = new Float32Array(totalPoints * 3);
  for (let k = 0; k < totalPoints; k++) {
    const px = targets[k * 3 + 0];
    const py = targets[k * 3 + 1];
    const pz = targets[k * 3 + 2];
    const v = THREE.MathUtils.clamp((py / height) * 0.5 + pz / 10 + 0.5, 0, 1);
    const col = new THREE.Color().setStyle(color).lerp(new THREE.Color('#fefffe'), v * 0.2);
    colors[k * 3 + 0] = col.r;
    colors[k * 3 + 1] = col.g;
    colors[k * 3 + 2] = col.b;
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

  const baseSize = 0.28;
  const material = new THREE.PointsMaterial({
    size: baseSize,
    sizeAttenuation: true,
    vertexColors: true,
    depthWrite: false,
    transparent: true,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
  });

  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;
  points.position.set(0, 0, 0);

  const group = new THREE.Group();
  group.add(points);

  // expose useful bits so the resizer can adapt material/scale
  group.userData.baseSize = baseSize;
  group.userData.points = points;

  points.userData._centers = centers;
  points.userData._dirs = dirs;
  points.userData._tangents = tangents;
  points.userData._radii = baseRadii;
  points.userData._jit = jitterFactors;
  points.userData._phases = phases;

  let loadActive = false;
  let loadStart = 0;
  let loadDuration = loadDurationDefault;
  let loadStagger = loadStaggerDefault;
  let breathPhaseOffset = 0;
  let spawnX = 0,
    spawnY = 0,
    spawnZ = 0;

  let loadTransitionActive = false;
  let loadTransitionStart = 0;
  let loadExitDuration = loadExitDurationDefault;
  const startPositions = new Float32Array(totalPoints * 3);

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const camera = cameraRef;
  const cameraDir = new THREE.Vector3();

  return {
    donut: group,

    startLoad(opts = {}) {
      loadDuration = typeof opts.duration === 'number' ? opts.duration : loadDurationDefault;
      loadStagger = typeof opts.stagger === 'number' ? opts.stagger : loadStaggerDefault;
      loadExitDuration = typeof opts.exitDuration === 'number' ? opts.exitDuration : loadExitDurationDefault;

      if (opts.spawn instanceof THREE.Vector3) {
        spawnX = opts.spawn.x;
        spawnY = opts.spawn.y;
        spawnZ = opts.spawn.z;
      } else if (pinnedToWall && camera) {
        camera.getWorldDirection(cameraDir);
        const sp = camera.position.clone().add(cameraDir.multiplyScalar(wallDistance));
        spawnX = sp.x;
        spawnY = sp.y;
        spawnZ = sp.z;
      } else {
        spawnX = 0;
        spawnY = 0;
        spawnZ = 0;
      }

      const posArr = geometry.attributes.position.array;
      for (let i = 0; i < totalPoints; i++) {
        posArr[i * 3 + 0] = spawnX;
        posArr[i * 3 + 1] = spawnY;
        posArr[i * 3 + 2] = spawnZ;
      }
      geometry.attributes.position.needsUpdate = true;

      loadStart = Date.now() * 0.001;
      loadActive = true;
      loadTransitionActive = false;

      breathPhaseOffset = Math.PI * 2 * breathSpeed * loadStart;
    },

    tick: () => {
      const dir = invertMouse ? -1 : 1;
      const tx = dir * mouseRef.targetX * moveRange;
      const ty = dir * mouseRef.targetY * moveRange;

      group.position.x += (tx - group.position.x) * followSmoothing;
      group.position.y += (ty - group.position.y) * followSmoothing;

      const tiltMax = 1;
      const tiltX = -mouseRef.targetY * tiltMax * dir; // you can choose whether tilt follows same dir
      const tiltY = mouseRef.targetX * tiltMax * dir;
      group.rotation.x += tiltX - group.rotation.x;
      group.rotation.y += tiltY - group.rotation.y;

      const now = Date.now() * 0.001;
      const b = 1 + Math.sin(now * Math.PI * 2 * breathSpeed - breathPhaseOffset) * breathAmplitude;

      const posAttr = geometry.attributes.position;
      const arr = posAttr.array;
      const centersLocal = points.userData._centers;
      const dirsLocal = points.userData._dirs;
      const tangLocal = points.userData._tangents;
      const radiiLocal = points.userData._radii;
      const jitLocal = points.userData._jit;
      const phaseLocal = points.userData._phases;
      const len = centersLocal.length;

      if (loadActive) {
        let completed = 0;
        for (let i = 0; i < len; i++) {
          const targetX = targets[i * 3 + 0];
          const targetY = targets[i * 3 + 1];
          const targetZ = targets[i * 3 + 2];

          const localT = THREE.MathUtils.clamp((now - loadStart - i * loadStagger) / loadDuration, 0, 1);
          const e = easeOutCubic(localT);

          arr[i * 3 + 0] = spawnX + (targetX - spawnX) * e;
          arr[i * 3 + 1] = spawnY + (targetY - spawnY) * e;
          arr[i * 3 + 2] = spawnZ + (targetZ - spawnZ) * e;

          if (localT >= 1) completed++;
        }
        posAttr.needsUpdate = true;

        if (completed === len) {
          loadActive = false;
          loadTransitionActive = true;
          loadTransitionStart = now;

          for (let i = 0; i < len; i++) {
            startPositions[i * 3 + 0] = arr[i * 3 + 0];
            startPositions[i * 3 + 1] = arr[i * 3 + 1];
            startPositions[i * 3 + 2] = arr[i * 3 + 2];
          }

          lockBreathing = 4;
          lockedSize = material.size;
        }

        return;
      }

      if (loadTransitionActive) {
        const tt = THREE.MathUtils.clamp((now - loadTransitionStart) / loadExitDuration, 0, 1);
        const ee = easeOutCubic(tt);

        const transitionBreath = 1 + Math.sin(now * Math.PI * 2 * breathSpeed - breathPhaseOffset) * breathAmplitude;

        for (let i = 0; i < len; i++) {
          const c = centersLocal[i];
          const d = dirsLocal[i];
          const t = tangLocal[i] || new THREE.Vector3(1, 0, 0);
          const ph = phaseLocal[i] || 0;

          const indie = Math.sin(now * Math.PI * 2 * moveFreq + ph) * moveAmp;
          const tanSlide = Math.sin(now * Math.PI * 2 * (moveFreq * 0.6) + ph * 0.9) * tanAmp;
          const centerOffsetX = c.x + t.x * tanSlide;
          const centerOffsetY = c.y + t.y * tanSlide;
          const centerOffsetZ = c.z + t.z * tanSlide;
          const r = radiiLocal[i] * transitionBreath * jitLocal[i] + indie;

          const desiredX = centerOffsetX + d.x * r;
          const desiredY = centerOffsetY + d.y * r;
          const desiredZ = centerOffsetZ + d.z * r;

          const sx = startPositions[i * 3 + 0];
          const sy = startPositions[i * 3 + 1];
          const sz = startPositions[i * 3 + 2];

          arr[i * 3 + 0] = sx + (desiredX - sx) * ee;
          arr[i * 3 + 1] = sy + (desiredY - sy) * ee;
          arr[i * 3 + 2] = sz + (desiredZ - sz) * ee;
        }

        posAttr.needsUpdate = true;

        material.size =
          baseSize * (1 + Math.sin(now * Math.PI * 2 * breathSpeed - breathPhaseOffset) * (breathAmplitude * 0.45));

        if (tt >= 1) {
          for (let i = 0; i < len; i++) {
            const c = centersLocal[i];
            const d = dirsLocal[i];
            const t = tangLocal[i] || new THREE.Vector3(1, 0, 0);
            const ph = phaseLocal[i] || 0;
            const indie = Math.sin(now * Math.PI * 2 * moveFreq + ph) * moveAmp;
            const tanSlide = Math.sin(now * Math.PI * 2 * (moveFreq * 0.6) + ph * 0.9) * tanAmp;
            const centerOffsetX = c.x + t.x * tanSlide;
            const centerOffsetY = c.y + t.y * tanSlide;
            const centerOffsetZ = c.z + t.z * tanSlide;
            const r = radiiLocal[i] * transitionBreath * jitLocal[i] + indie;
            arr[i * 3 + 0] = centerOffsetX + d.x * r;
            arr[i * 3 + 1] = centerOffsetY + d.y * r;
            arr[i * 3 + 2] = centerOffsetZ + d.z * r;
          }
          posAttr.needsUpdate = true;
          loadTransitionActive = false;
        } else {
          return;
        }
      }

      for (let i = 0; i < len; i++) {
        const c = centersLocal[i];
        const d = dirsLocal[i];
        const t = tangLocal[i] || new THREE.Vector3(1, 0, 0);

        const ph = phaseLocal[i] || 0;
        const indie = Math.sin(now * Math.PI * 2 * moveFreq + ph) * moveAmp;
        const tanSlide = Math.sin(now * Math.PI * 2 * (moveFreq * 0.6) + ph * 0.9) * tanAmp;

        const centerOffsetX = c.x + t.x * tanSlide;
        const centerOffsetY = c.y + t.y * tanSlide;
        const centerOffsetZ = c.z + t.z * tanSlide;

        const r = radiiLocal[i] * b * jitLocal[i] + indie;

        arr[i * 3 + 0] = centerOffsetX + d.x * r;
        arr[i * 3 + 1] = centerOffsetY + d.y * r;
        arr[i * 3 + 2] = centerOffsetZ + d.z * r;
      }

      posAttr.needsUpdate = true;

      material.size =
        baseSize * (1 + Math.sin(now * Math.PI * 2 * breathSpeed - breathPhaseOffset) * (breathAmplitude * 0.45));

      if (pinnedToWall && camera) {
        camera.getWorldDirection(cameraDir);
        const targetPos = camera.position.clone().add(cameraDir.multiplyScalar(wallDistance));
        points.position.lerp(targetPos, 0.25);
        points.quaternion.copy(camera.quaternion);
      }
    },
  };
};

/* ---------- World / Loop / Resizer (improved for responsiveness) ---------- */

class World {
  constructor(container) {
    this.container = container;
    this.scene = createScene();
    this.camera = createCamera();
    this.renderer = createRenderer();

    this._baseCameraZ = this.camera.position.z; // remember original camera distance
    this._lastScale = 1;

    this.mouse = { targetX: 0, targetY: 0 };

    const width = 200;
    const height = 80;

    this.donut = createRoundedRectTube({
      width: width,
      height: height,
      cornerRadius: height / 2,
      tubeRadius: height / 8,
      pathSeg: height * 2,
      circSeg: 48,
      color: '#04080f',
      moveRange: 0.06,
      followSmoothing: 0.12,
      mouseRef: this.mouse,
      breathAmplitude: 0.08,
      breathSpeed: 0.4,
      perPointJitter: 0.25,
      fillInterior: true,
      radialPoints: 16,
      pinnedToWall: true,
      cameraRef: this.camera,
      wallDistance: 150,
      separation: 1.5,
      moveAmp: 0.2,
      moveFreq: 2,
      tanAmp: 1,
      invertMouse: false,
    });

    this.lights = createLights();
    this.scene.add(this.lights.ambientLight, this.lights.mainLight, this.donut.donut);

    this.controls = createControls(this.camera, this.renderer.domElement);
    this.loop = new Loop(this.camera, this.scene, this.renderer);
    this.loop.updatables.push(this.controls);
    this.loop.updatables.push(this.donut);

    container.appendChild(this.renderer.domElement);

    // pass an onResize callback so the world can rescale content
    this.resizer = new Resizer(container, this.camera, this.renderer, () => this._onResize());

    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    const spawnPoint = this.camera.position.clone().add(dir.multiplyScalar(0));

    this.donut.startLoad({
      duration: 2.5,
      stagger: 0,
      spawn: spawnPoint,
      exitDuration: 0.5,
    });

    this._onPointerMove = this._onPointerMove.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    if (!('ontouchstart' in window)) {
      window.addEventListener('mousemove', this._onPointerMove, { passive: true });
    }
  }

  _onPointerMove(e) {
    const rect = this.container.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    const sensitivity = 0.12;
    this.mouse.targetX = Math.max(-1, Math.min(1, nx * sensitivity));
    this.mouse.targetY = Math.max(-1, Math.min(1, ny * sensitivity));
  }

  _onTouchMove(e) {
    if (!e.touches || e.touches.length === 0) return;
    const t = e.touches[0];
    this._onPointerMove(t);
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }

  async init() {
    this.controls.orbitControls.target.copy(this.donut.donut.position);
  }

  start() {
    this.loop.start();
  }

  stop() {
    this.loop.stop();
  }

  dispose() {
    window.removeEventListener('mousemove', this._onPointerMove);
    window.removeEventListener('touchmove', this._onTouchMove);
    if (this.resizer) this.resizer.dispose();
  }

  // called by Resizer when container size changes
  _onResize() {
    // compute a scale factor from the container size — tune the reference values to taste
    const w = this.container.clientWidth || window.innerWidth;
    const h = this.container.clientHeight || window.innerHeight;
    const refW = 1200;
    const refH = 700;

    // keep scale within sane bounds
    let s = Math.min(w / refW, h / refH);
    s = Math.max(0.6, Math.min(1.6, s));

    // apply to group
    this.donut.donut.scale.setScalar(s);

    // adjust camera distance so scaling the group preserves apparent size
    this.camera.position.z = this._baseCameraZ / s;
    this.camera.updateProjectionMatrix();

    // gently update points size relative to previous scale to avoid jumps
    const points = this.donut.donut.userData.points;
    if (points && points.material) {
      const ratio = s / this._lastScale;
      points.material.size = (points.material.size || this.donut.donut.userData.baseSize) * ratio;
      points.material.needsUpdate = true;
    }

    this._lastScale = s;
  }
}

class Loop {
  constructor(camera, scene, renderer) {
    this.camera = camera;
    this.scene = scene;
    this.renderer = renderer;
    this.updatables = [];
  }

  tick() {
    this.updatables.forEach((u) => u.tick && u.tick());
  }

  start() {
    this.renderer.setAnimationLoop(() => {
      this.tick();
      this.renderer.render(this.scene, this.camera);
    });
  }

  stop() {
    this.renderer.setAnimationLoop(null);
  }
}

class Resizer {
  constructor(container, camera, renderer, onResize) {
    this.container = container;
    this.camera = camera;
    this.renderer = renderer;
    this.onResize = onResize;

    this._onResize = () => {
      this.setSize(this.container, this.camera, this.renderer);
      if (this.onResize) this.onResize();
    };

    // use ResizeObserver for element size changes (better and more reliable than window resize only)
    this._ro = new ResizeObserver(() => {
      this._onResize();
    });
    this._ro.observe(this.container);

    // fallback for older browsers / orientation changes
    window.addEventListener('resize', this._onResize, { passive: true });

    // initial size
    this._onResize();
  }

  setSize(container, camera, renderer) {
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;

    camera.aspect = w / h;
    camera.updateProjectionMatrix();

    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    // set drawing buffer to match container size (third arg false keeps CSS width/height untouched)
    renderer.setSize(w, h, false);

    // keep CSS to 100% so layout fills the parent
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
  }

  dispose() {
    if (this._ro) {
      try {
        this._ro.disconnect();
      } catch (e) {}
      this._ro = null;
    }
    window.removeEventListener('resize', this._onResize);
  }
}

/* ---------- mount / lifecycle ---------- */
let worldInstance = null;

onMounted(() => {
  const main = async () => {
    const container = document.getElementById('vortec-logo-animation');
    if (!container) throw new Error('vortec-logo-animation not found');

    worldInstance = new World(container);
    await worldInstance.init();
    worldInstance.start();
  };

  main().catch((err) => console.error(err));
});

onUnmounted(() => {
  if (worldInstance) {
    worldInstance.stop();
    worldInstance.dispose && worldInstance.dispose();
    worldInstance = null;
  }
});
</script>

<style lang="scss">
#vortec-logo-animation {
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  overflow: hidden;
  flex: 1 1 auto;

  canvas {
    position: absolute;
    inset: 0;
    display: block;
    width: 100% !important;
    height: 100% !important;
  }
}
</style>
